import { useEffect, useState } from 'react'

/**
 * Breakpoints.
 *
 * One systematic set, shared by the CSS and the components that can't be done
 * in CSS alone (a split pane whose columns become a stack, a sidebar that
 * becomes a tab bar). Two sources of truth for "when is this mobile" is how a
 * layout ends up half-switched at 760px.
 */
export const BREAKPOINTS = {
  /** Phone. Below this the app is a single column with a bottom tab bar. */
  mobile: 720,
  /** Tablet / small laptop. Side rails collapse under the main column. */
  compact: 1024,
} as const

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )

  useEffect(() => {
    const list = window.matchMedia(query)
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches)
    setMatches(list.matches)
    list.addEventListener('change', onChange)
    return () => list.removeEventListener('change', onChange)
  }, [query])

  return matches
}

/** True on a phone-width viewport. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`)
}

/** True when there isn't room for a main column plus a side rail. */
export function useIsCompact(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.compact}px)`)
}
